// src/components/roads/RoadWorkOrderQaModal.js
// --- UPDATE (FlowSense road AI/ML pass) ---------------------------------------
//   • Inline validation (IRI 0–20 m/km, density 50–100 %) — no NaN audits.
//   • Form resets when the modal is opened for a different work order.
//   • Result panel is null-safe (reasons[] could be missing → crash) and dark-mode safe,
//     shows which engine decided (ML model / FlowSense rules) and the linked checklist.
//   • Audit history for the job; contractor pre-filled from the last audit.
//   • "Open QA checklist" (tick-box acceptance checks) and, on rework, "Create rework
//     work order" (typed road draft, parent = this job).
//   • Publishes work-order / road changes so every FlowSense page refreshes.
// --- END UPDATE -----------------------------------------------------------------
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, FormControl, FormLabel, FormErrorMessage, FormHelperText, Input, Textarea, VStack, HStack,
    Badge, Box, Text, useToast, useColorModeValue, Icon, SimpleGrid, Collapse,
} from '@chakra-ui/react';
import { MdVerified, MdHistory, MdChecklist, MdBuild } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './page/shared';
import { openCreateWorkOrder } from '../work_orders/workOrderCatalog';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
import { roadFollowUpDraft } from './roadAiIntel';

// --- UPDATE: ML vocabulary (flagged_for_penalty); conditional_approval kept for older audits
const STATUS_COLOR = { approved: 'green', flagged_for_penalty: 'yellow', conditional_approval: 'yellow', rework_mandated: 'red' };
const STATUS_LABEL = { approved: 'APPROVED', flagged_for_penalty: 'FLAGGED · PAYMENT RETENTION', conditional_approval: 'FLAGGED · PAYMENT RETENTION', rework_mandated: 'REWORK MANDATED' };
// --- END UPDATE
const label = (s) => STATUS_LABEL[s] || String(s || '').replace(/_/g, ' ').toUpperCase(); // --- UPDATE

/** Field validation — exported for tests. */
export const validateQaInputs = ({ smoothnessIri, compactionDensity }) => {
    const errors = {};
    const iri = Number(smoothnessIri);
    const den = Number(compactionDensity);
    if (smoothnessIri === '' || !Number.isFinite(iri) || iri < 0 || iri > 20) errors.iri = 'Enter IRI between 0 and 20 m/km.';
    if (compactionDensity === '' || !Number.isFinite(den) || den < 50 || den > 100) errors.density = 'Enter density between 50 and 100 %.';
    return errors;
};

const RoadWorkOrderQaModal = ({ isOpen, onClose, workOrder, onAuditComplete, road, onOpenChecklist }) => {
    const [smoothnessIri, setSmoothnessIri] = useState('1.6');
    const [compactionDensity, setCompactionDensity] = useState('94.5');
    const [contractorName, setContractorName] = useState('');
    const [auditNotes, setAuditNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [decisionResult, setDecisionResult] = useState(null);
    // --- UPDATE
    const [touched, setTouched] = useState(false);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const navigate = useNavigate();
    // --- END UPDATE

    const toast = useToast();
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    // --- UPDATE: dark-mode safe result colours
    const okBg = useColorModeValue('green.50', 'rgba(72,187,120,0.12)');
    const warnBg = useColorModeValue('yellow.50', 'rgba(236,201,75,0.12)');
    const badBg = useColorModeValue('red.50', 'rgba(245,101,101,0.12)');
    const muted = useColorModeValue('gray.600', 'gray.400');
    // --- END UPDATE

    const woId = workOrder?.id;
    const errors = validateQaInputs({ smoothnessIri, compactionDensity });
    const hasErrors = Object.keys(errors).length > 0;

    // --- UPDATE: new job → fresh form + its audit history
    const loadHistory = useCallback(async () => {
        if (!woId) return;
        try {
            const res = await apiFetch(`/roads/work-orders/${encodeURIComponent(woId)}/audits`);
            const rows = res?.data || [];
            if (!mounted.current) return;
            setHistory(rows);
            if (rows[0]?.contractor_name) setContractorName((c) => c || rows[0].contractor_name);
        } catch {
            if (mounted.current) setHistory([]);
        }
    }, [woId]);

    useEffect(() => {
        if (!isOpen) return;
        setDecisionResult(null);
        setTouched(false);
        setAuditNotes('');
        setContractorName('');
        setSmoothnessIri('1.6');
        setCompactionDensity('94.5');
        loadHistory();
    }, [isOpen, woId, loadHistory]);
    // --- END UPDATE

    const handleRunAudit = async () => {
        setTouched(true);
        if (hasErrors || !woId) return; // --- UPDATE
        setIsSubmitting(true);
        try {
            const payload = {
                contractor_name: contractorName || 'Internal Public Works Crew',
                pavement_smoothness_iri: Number(smoothnessIri),
                compaction_density_pct: Number(compactionDensity),
                audit_notes: auditNotes
            };

            const res = await apiFetch(`/roads/work-orders/${encodeURIComponent(woId)}/audit`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (res?.success && res.data) {
                // --- UPDATE: normalise before rendering
                const data = { ...res.data, reasons: Array.isArray(res.data.reasons) ? res.data.reasons : [] };
                setDecisionResult(data);
                const status = String(data.ai_approval_status || '');
                toast({
                    title: 'Audit Complete',
                    description: `Job classified as: ${label(status)}${data.engine === 'rules' ? ' (FlowSense rules — ML offline)' : ''}`,
                    status: status === 'approved' ? 'success' : 'warning',
                    duration: 5000,
                    isClosable: true
                });
                publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { id: woId, action: 'qa_audit', status });
                if (road?.id || workOrder?.road_id) publishBus(BUS_EVENTS.ROAD_CHANGED, { id: road?.id || workOrder.road_id, action: 'qa_audit' });
                loadHistory();
                // --- END UPDATE
                if (onAuditComplete) onAuditComplete(data);
            } else {
                throw new Error(res?.message || 'Audit failed.');
            }
        } catch (error) {
            toast({
                title: 'Audit Failed',
                description: error.message || 'Could not communicate with ML audit engine.',
                status: 'error',
                duration: 4000
            });
        } finally {
            if (mounted.current) setIsSubmitting(false);
        }
    };

    // --- UPDATE: rework follow-up
    const createRework = () => {
        const draft = roadFollowUpDraft({
            road: road || { id: workOrder?.road_id || workOrder?.asset_id, name: workOrder?.location_description },
            followUp: { category: workOrder?.category || 'Road Paving', type: 'corrective' },
            parentWorkOrder: workOrder,
            priority: 'high',
            description: [`Rework mandated by QA audit ${decisionResult?.auditId || ''} on "${workOrder?.title || woId}".`, ...(decisionResult?.reasons || []).map((r) => `- ${r}`)].join('\n'),
        });
        draft.title = `Rework: ${workOrder?.title || draft.title}`;
        onClose();
        openCreateWorkOrder(navigate, draft);
    };
    const resultBg = decisionResult ? (decisionResult.ai_approval_status === 'approved' ? okBg : decisionResult.ai_approval_status === 'rework_mandated' ? badBg : warnBg) : undefined;
    // --- END UPDATE

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(5px)" />
            <ModalContent borderRadius="2xl" bg={cardBg}>
                <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
                    <HStack spacing={2}>
                        <Icon as={MdVerified} color="teal.500" boxSize={6} />
                        <Box>
                            <Text fontSize="md" fontWeight="800">AI Workmanship & Paving QA Audit</Text>
                            <Text fontSize="xs" color="gray.500">WO: {workOrder?.title || workOrder?.id}</Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={5}>
                    <VStack spacing={4} align="stretch">
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700">Contractor / Crew</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder="e.g. Caribbean Civil Works Ltd"
                                    value={contractorName}
                                    onChange={(e) => setContractorName(e.target.value)}
                                    maxLength={120}
                                />
                                <FormHelperText fontSize="2xs">Blank = Internal Public Works Crew</FormHelperText>
                            </FormControl>
                            <FormControl isRequired isInvalid={touched && !!errors.density}>
                                <FormLabel fontSize="xs" fontWeight="700">Compaction Density (%)</FormLabel>
                                <Input
                                    size="sm"
                                    type="number"
                                    step="0.1"
                                    placeholder="94.5"
                                    value={compactionDensity}
                                    onChange={(e) => setCompactionDensity(e.target.value)}
                                />
                                {touched && errors.density ? <FormErrorMessage fontSize="2xs">{errors.density}</FormErrorMessage> : <FormHelperText fontSize="2xs">Spec ≥ 92 %</FormHelperText>}
                            </FormControl>
                        </SimpleGrid>

                        <FormControl isRequired isInvalid={touched && !!errors.iri}>
                            <FormLabel fontSize="xs" fontWeight="700">Pavement Smoothness (IRI m/km)</FormLabel>
                            <Input
                                size="sm"
                                type="number"
                                step="0.1"
                                placeholder="1.6"
                                value={smoothnessIri}
                                onChange={(e) => setSmoothnessIri(e.target.value)}
                            />
                            {touched && errors.iri ? <FormErrorMessage fontSize="2xs">{errors.iri}</FormErrorMessage> : <FormHelperText fontSize="2xs">Spec ≤ 2.0 m/km (lower is smoother)</FormHelperText>}
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="xs" fontWeight="700">Inspector Verification Notes</FormLabel>
                            <Textarea
                                size="sm"
                                rows={3}
                                placeholder="Core samples, visual defect scan, joint compaction notes..."
                                value={auditNotes}
                                onChange={(e) => setAuditNotes(e.target.value)}
                                maxLength={2000}
                            />
                        </FormControl>

                        {/* --- UPDATE: tick-box acceptance checklist for this job */}
                        {onOpenChecklist && (
                            <Button size="sm" variant="outline" leftIcon={<Icon as={MdChecklist} />} onClick={() => onOpenChecklist(workOrder)}>
                                Open QA checklist (tick boxes, measurements, notes)
                            </Button>
                        )}

                        {decisionResult && (
                            <Box p={4} borderRadius="xl" borderWidth="1px" borderColor={`${STATUS_COLOR[decisionResult.ai_approval_status] || 'gray'}.300`} bg={resultBg} aria-live="polite">
                                <HStack justify="space-between" mb={2}>
                                    <Text fontWeight="800" fontSize="sm">AI Evaluation Decision</Text>
                                    <HStack spacing={1}>
                                        <Badge colorScheme={decisionResult.engine === 'rules' ? 'gray' : 'purple'} fontSize="2xs">{decisionResult.engine === 'rules' ? 'FlowSense rules' : 'ML model'}</Badge>
                                        <Badge colorScheme={STATUS_COLOR[decisionResult.ai_approval_status] || 'gray'}>
                                            {label(decisionResult.ai_approval_status)}
                                        </Badge>
                                    </HStack>
                                </HStack>
                                <Text fontSize="xs" fontWeight="600" mb={2}>
                                    Quality Score: <b>{decisionResult.ai_quality_score ?? '—'}/100</b> • Rating: <b>{decisionResult.workmanship_rating || '—'}</b>
                                    {decisionResult.material_variance_pct != null && <> • Material variance: <b>{decisionResult.material_variance_pct}%</b></>}
                                </Text>
                                {decisionResult.decision && <Text fontSize="xs" color={muted} mb={2}>{decisionResult.decision}</Text>}
                                <VStack align="stretch" spacing={1}>
                                    {(decisionResult.reasons || []).map((r, i) => (
                                        <Text key={i} fontSize="2xs" color={muted}>• {r}</Text>
                                    ))}
                                </VStack>
                                {decisionResult.checklist && <Text fontSize="2xs" mt={2} color={muted}>Linked checklist score: {decisionResult.checklist.score ?? '—'} ({decisionResult.checklist.rating || 'n/a'})</Text>}
                                {decisionResult.ai_approval_status === 'rework_mandated' && (
                                    <Button mt={3} size="xs" colorScheme="red" leftIcon={<Icon as={MdBuild} />} onClick={createRework}>Create rework work order</Button>
                                )}
                            </Box>
                        )}

                        {/* --- UPDATE: audit history */}
                        {history.length > 0 && (
                            <Box>
                                <Button size="xs" variant="ghost" leftIcon={<Icon as={MdHistory} />} onClick={() => setShowHistory((v) => !v)}>
                                    {showHistory ? 'Hide' : 'Show'} audit history ({history.length})
                                </Button>
                                <Collapse in={showHistory} animateOpacity>
                                    <VStack align="stretch" spacing={1} mt={2}>
                                        {history.map((h) => (
                                            <HStack key={h.id} justify="space-between" fontSize="xs" borderBottomWidth="1px" borderColor={borderColor} py={1}>
                                                <Text>{h.created_at ? new Date(h.created_at).toLocaleDateString() : h.id} · {h.contractor_name || h.internal_tech_id || 'crew'}</Text>
                                                <HStack spacing={1}>
                                                    <Text>{h.ai_quality_score ?? '—'}/100</Text>
                                                    <Badge colorScheme={STATUS_COLOR[h.ai_approval_status] || 'gray'} fontSize="2xs">{label(h.ai_approval_status)}</Badge>
                                                </HStack>
                                            </HStack>
                                        ))}
                                    </VStack>
                                </Collapse>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                    <Button variant="ghost" mr={3} onClick={onClose} size="sm">Close</Button>
                    <Button
                        colorScheme="teal"
                        leftIcon={<Icon as={MdVerified} />}
                        onClick={handleRunAudit}
                        isLoading={isSubmitting}
                        loadingText="Auditing…"
                        isDisabled={!woId || (touched && hasErrors)}
                        size="sm"
                    >
                        Execute AI QA Audit
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

RoadWorkOrderQaModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    workOrder: PropTypes.object,
    onAuditComplete: PropTypes.func,
    road: PropTypes.object,
    onOpenChecklist: PropTypes.func,
};

export default RoadWorkOrderQaModal;
